/**
 * Tab „Veröffentlichen" einer Veranstaltung (iMikel-Layout).
 *
 * Schaltet den Kurs für die Online-Anmeldung frei und pflegt die Texte, die
 * dort im Dialog „Kursangebote" erscheinen. Gespeichert wird alles gebündelt
 * über PUT /courses/{id}/publication.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Save, Trash2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DateInput from '@/components/ui/date-input';
import { formatTime } from '@/lib/utils';
import SubTabs from './SubTabs';

const SUB_TABS = [
  { value: 'allgemeines', label: 'Allgemeines' },
  { value: 'termine', label: 'Termine' },
  { value: 'vorkenntnisse', label: 'Vorkenntnisse' },
  { value: 'zielgruppe', label: 'Zielgruppe' },
  { value: 'voraussetzung', label: 'Voraussetzung' },
  { value: 'material', label: 'Material' },
  { value: 'ansprechpartner', label: 'Ansprechpartner' },
  { value: 'bemerkung', label: 'Bemerkung' },
];

const GENERAL_FLAGS = [
  { key: 'hide_schedule', label: 'Beginn/Ende ausblenden' },
  { key: 'hide_teacher', label: 'Lehrkraft ausblenden' },
  { key: 'hide_room', label: 'Raum ausblenden' },
  { key: 'allow_overbooking', label: 'Überbuchung zulassen' },
  { key: 'show_fee', label: 'Gebühr anzeigen' },
  { key: 'skip_bank_details', label: 'Keine Bankverbindung abfragen' },
];

/** Reine Textfelder je Unter-Tab — Reihenfolge = Reihenfolge der Tabs. */
const TEXT_TABS = {
  vorkenntnisse: { field: 'prior_knowledge', label: 'Vorkenntnisse', rows: 6, placeholder: 'z. B. keine Vorkenntnisse erforderlich' },
  zielgruppe: { field: 'target_group', label: 'Zielgruppe', rows: 4, placeholder: 'z. B. ab ca. 4 Jahren' },
  voraussetzung: { field: 'requirements', label: 'Voraussetzung', rows: 6, placeholder: 'z. B. eigenes Instrument' },
  material: { field: 'material', label: 'Material', rows: 6, placeholder: 'z. B. Notenheft wird gestellt' },
  ansprechpartner: { field: 'contact_person', label: 'Ansprechpartner', rows: 4, placeholder: 'Name, Telefon, E-Mail' },
  bemerkung: { field: 'remarks', label: 'Bemerkung', rows: 6, placeholder: 'z. B. Die Gruppengröße ist begrenzt.' },
};

const EMPTY = {
  online_registration: false, web_builder: false,
  hide_schedule: false, hide_teacher: false, hide_room: false,
  allow_overbooking: false, show_fee: false, skip_bank_details: false,
  description: '', schedule_text: '', period_text: '', time_from: '', time_to: '',
  prior_knowledge: '', target_group: '', requirements: '', material: '',
  contact_person: '', remarks: '',
  availability_mode: 'auto', registration_deadline: null, dates: [],
};

const Check = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      data-testid={testId}
      className="h-3.5 w-3.5 accent-[color:var(--brand-red,#9b1c1c)] cursor-pointer"
    />
    <span>{label}</span>
  </label>
);

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="text-[10px] text-slate-500 uppercase tracking-wide leading-none block mb-1">{label}</label>
    {children}
  </div>
);

const inputClass = 'w-full text-xs border border-slate-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-brand-red/40';

const TextArea = ({ value, onChange, rows = 4, placeholder, testId }) => (
  <textarea
    rows={rows}
    value={value || ''}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className={inputClass}
  />
);

export default function CoursePublishTab({ courseId, subTab, onSubTabChange }) {
  const [tab, setTab] = useState('allgemeines');
  const [pub, setPub] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [newDate, setNewDate] = useState('');

  const activeTab = subTab || tab;
  const changeTab = onSubTabChange || setTab;

  useEffect(() => {
    if (!courseId) { setPub(EMPTY); setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    api.get(`/courses/${courseId}/publication`)
      .then((r) => { if (!cancelled) { setPub({ ...EMPTY, ...(r.data || {}) }); setDirty(false); } })
      .catch(() => { if (!cancelled) setPub(EMPTY); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [courseId]);

  const set = useCallback((patch) => {
    setPub((p) => ({ ...p, ...patch }));
    setDirty(true);
  }, []);

  const save = async () => {
    if (!courseId) return;
    setSaving(true);
    try {
      const r = await api.put(`/courses/${courseId}/publication`, pub);
      setPub({ ...EMPTY, ...(r.data || {}) });
      setDirty(false);
      toast.success('Veröffentlichung gespeichert');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const addDate = () => {
    const iso = newDate;
    setNewDate('');
    if (!iso || (pub.dates || []).includes(iso)) return;
    set({ dates: [...(pub.dates || []), iso].sort() });
  };
  const removeDate = (iso) => set({ dates: (pub.dates || []).filter((d) => d !== iso) });

  if (!courseId) {
    return (
      <div className="p-4 text-xs text-slate-500" data-testid="publish-no-course">
        Keine Veranstaltung geöffnet.
      </div>
    );
  }
  if (loading) {
    return <div className="p-4 text-xs text-slate-500" data-testid="publish-loading">Veröffentlichung wird geladen …</div>;
  }

  return (
    <div data-testid="course-publish-tab">
      <div className="flex flex-wrap items-center gap-6 px-4 py-3 border-b border-slate-200 bg-slate-50/60">
        <Check
          label="Online-Anmeldung"
          checked={pub.online_registration}
          onChange={(v) => set({ online_registration: v })}
          testId="publish-online-registration"
        />
        <Check
          label="Web-Builder"
          checked={pub.web_builder}
          onChange={(v) => set({ web_builder: v })}
          testId="publish-web-builder"
        />
        <span
          className={`text-[11px] px-2 py-0.5 rounded ${pub.online_registration ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}
          data-testid="publish-state-badge"
        >
          {pub.online_registration
            ? 'Erscheint unter „Kursangebote“'
            : 'Nicht in der Online-Anmeldung sichtbar'}
        </span>
        <Button
          size="sm"
          className="ml-auto h-7 text-xs"
          onClick={save}
          disabled={saving || !dirty}
          data-testid="publish-save"
        >
          <Save className="h-3.5 w-3.5 mr-1" />
          {saving ? 'Speichern…' : 'Speichern'}
        </Button>
      </div>

      <SubTabs value={activeTab} onChange={changeTab} items={SUB_TABS} />

      <div className="p-4">
        {activeTab === 'allgemeines' && (
          <div className="space-y-4" data-testid="publish-pane-allgemeines">
            <Field label="Beschreibung">
              <TextArea
                rows={6}
                value={pub.description}
                onChange={(v) => set({ description: v })}
                placeholder="Text, der im Kursangebot erscheint"
                testId="publish-description"
              />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-2">
              {GENERAL_FLAGS.map((f) => (
                <Check
                  key={f.key}
                  label={f.label}
                  checked={pub[f.key]}
                  onChange={(v) => set({ [f.key]: v })}
                  testId={`publish-${f.key.replace(/_/g, '-')}`}
                />
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
              <Field label="Auslastung als Ampel darstellen">
                <select
                  value={pub.availability_mode || 'auto'}
                  onChange={(e) => set({ availability_mode: e.target.value })}
                  data-testid="publish-availability-mode"
                  className={inputClass}
                >
                  <option value="auto">Aus Belegung berechnen</option>
                  <option value="free">Plätze frei</option>
                  <option value="few">Wenige Plätze</option>
                  <option value="full">Belegt / Warteliste</option>
                  <option value="hidden">Nicht anzeigen</option>
                </select>
              </Field>
              <Field label="Anmeldeschluss">
                <DateInput
                  value={pub.registration_deadline || ''}
                  onChange={(e) => set({ registration_deadline: e.target.value || null })}
                  data-testid="publish-registration-deadline"
                  className="text-xs h-7"
                />
              </Field>
            </div>
          </div>
        )}

        {activeTab === 'termine' && (
          <div className="space-y-4 max-w-2xl" data-testid="publish-pane-termine">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Uhrzeit von">
                <input
                  type="text"
                  value={formatTime(pub.time_from) || pub.time_from || ''}
                  placeholder="10:25"
                  onChange={(e) => set({ time_from: e.target.value })}
                  data-testid="publish-time-from"
                  className={inputClass}
                />
              </Field>
              <Field label="Uhrzeit bis">
                <input
                  type="text"
                  value={formatTime(pub.time_to) || pub.time_to || ''}
                  placeholder="12:00"
                  onChange={(e) => set({ time_to: e.target.value })}
                  data-testid="publish-time-to"
                  className={inputClass}
                />
              </Field>
              <Field label="Zeitraum">
                <input
                  type="text"
                  value={pub.period_text || ''}
                  placeholder="01.09.2026 bis 30.09.2026"
                  onChange={(e) => set({ period_text: e.target.value })}
                  data-testid="publish-period-text"
                  className={inputClass}
                />
              </Field>
            </div>

            <Field label="Einzeltermine">
              <div className="flex items-center gap-2">
                <DateInput
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  data-testid="publish-date-add"
                  className="text-xs h-7 w-40"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => addDate()}
                  disabled={!newDate}
                  data-testid="publish-date-add-btn"
                >
                  <Plus className="h-3 w-3 mr-1" /> Aufnehmen
                </Button>
              </div>
              {(pub.dates || []).length === 0 ? (
                <p className="text-xs text-slate-400 italic mt-2" data-testid="publish-dates-empty">
                  Keine Einzeltermine — es gilt der Termin aus dem Stundenplan.
                </p>
              ) : (
                <ul className="mt-2 divide-y divide-slate-100 border border-slate-200 rounded" data-testid="publish-dates-list">
                  {(pub.dates || []).map((d) => (
                    <li key={d} className="flex items-center justify-between px-2 py-1 text-xs">
                      <span>{d.split('-').reverse().join('.')}</span>
                      <button
                        type="button"
                        onClick={() => removeDate(d)}
                        data-testid={`publish-date-remove-${d}`}
                        className="text-rose-500 hover:text-rose-700"
                        aria-label={`Termin ${d} entfernen`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-[10px] text-slate-500 mt-1" data-testid="publish-dates-count">
                {(pub.dates || []).length} Termine
              </p>
            </Field>

            <Field label="Termin-Text (überschreibt die Anzeige)">
              <input
                type="text"
                value={pub.schedule_text || ''}
                placeholder="Samstag, 10:25 - 12:00 Uhr, 4 Termine"
                onChange={(e) => set({ schedule_text: e.target.value })}
                data-testid="publish-schedule-text"
                className={inputClass}
              />
            </Field>
          </div>
        )}

        {TEXT_TABS[activeTab] && (
          <div className="max-w-2xl" data-testid={`publish-pane-${activeTab}`}>
            <Field label={TEXT_TABS[activeTab].label}>
              <TextArea
                rows={TEXT_TABS[activeTab].rows}
                value={pub[TEXT_TABS[activeTab].field]}
                onChange={(v) => set({ [TEXT_TABS[activeTab].field]: v })}
                placeholder={TEXT_TABS[activeTab].placeholder}
                testId={`publish-${activeTab}`}
              />
            </Field>
          </div>
        )}
      </div>
    </div>
  );
}
